import * as React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Checkbox,
  Text,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  ArrowLeft24Regular,
} from "@fluentui/react-icons";
import DiscussionTypeSelector from "./DiscussionTypeSelector";
import DiscussionForm, { MAX_DESCRIPTION_LENGTH } from "./DiscussionForm";
import TagSelector from "./TagSelector";
import StatusItemSelector from "./StatusItemSelector";
import StagedAttachments from "./StagedAttachments";
import { createDiscussion, CreateDiscussionRequest } from "../../../services/discussions.service";
import { uploadDiscussionAttachment } from "../../../services/discussion-attachments.service";
import { useFeatureFlags } from "../../../contexts/FeatureFlagContext";
import { getStatusItemsBySpreadsheet, StatusItemDto } from "../../../services/status-items.service";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { useWorkflowIntelligence } from "../../../contexts/WorkflowIntelligenceContext";
import { UserRole } from "../../../types/UserRole";
import {
  MAIN_DISCUSSION_TYPES,
  getTagsForMainType,
  findTypeById,
} from "../types/discussionTypes";
import { RichTextValue, MentionUser } from "./richtext/types";
import { extractUserEmails } from "./richtext/parser";
import { ExcelService } from "../../../services/excel.service";
import { useTrackEvent } from "../../../hooks/useTrackEvent";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "600px",
    minWidth: "400px",
  },
  selectedTypeHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  typeIcon: {
    width: "24px",
    height: "24px",
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  dialogActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  createAnotherCheckbox: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  actionButtons: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
  },
});

interface CreateDiscussionDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  spreadsheetId: string;
  users?: MentionUser[];
  onSuccess: () => void;
  initialType?: string | null;
}

/**
 * Modal for creating a new discussion. Two-step flow: pick the discussion
 * type (review point / QC / learning / etc.), then fill out the form.
 */
const CreateDiscussionDialog: React.FC<CreateDiscussionDialogProps> = ({
  isOpen,
  onDismiss,
  spreadsheetId,
  users,
  onSuccess,
  initialType,
}) => {
  const styles = useStyles();
  const { userRole, isStandalone } = useWorkflowIntelligence();
  const track = useTrackEvent();

  // State
  const [step, setStep] = useState(1);
  const [selectedMainType, setSelectedMainType] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState<RichTextValue>({ markup: "", plainText: "" });
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [statusItems, setStatusItems] = useState<StatusItemDto[]>([]);
  const [createAnother, setCreateAnother] = useState(false);
  const [selectedStatusItemIds, setSelectedStatusItemIds] = useState<number[]>([]);
  // Attachments can only be uploaded against an existing discussion ID, so the
  // wizard holds the chosen Files here and uploads them after create returns.
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  // Set once the discussion exists. Guards the retry path: if the create
  // succeeded but an attachment upload didn't, pressing Create again must
  // retry the uploads rather than create a second discussion.
  const [createdDiscussionId, setCreatedDiscussionId] = useState<number | null>(null);
  const [activeSheetId, setActiveSheetId] = useState<string | null>(null);
  const { enableDiscussionAttachments } = useFeatureFlags();

  // Fetch status items and active worksheet when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      try {
        // Auto-link uses the active sheet's ID — skip in standalone (no Excel
        // context to read it from). Users on standalone can still link status
        // items manually through the multi-select below.
        const [items, worksheetInfo] = await Promise.all([
          getStatusItemsBySpreadsheet(spreadsheetId),
          isStandalone
            ? Promise.resolve<{ id: string } | null>(null)
            : ExcelService.getActiveWorksheetInfo(),
        ]);
        // Hidden items shouldn't appear in the linker dropdown — and we also
        // don't want the active-sheet auto-link below to silently attach a
        // hidden item the user can't see to unlink.
        setStatusItems(items.filter((i) => !i.isHidden));
        setActiveSheetId(worksheetInfo?.id ?? null);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setStatusItems([]);
      }
    };

    fetchData();
  }, [isOpen, spreadsheetId, isStandalone]);

  // Handle initial type selection when dialog opens with a pre-selected type
  useEffect(() => {
    if (isOpen && initialType) {
      setSelectedMainType(initialType);
      // Go directly to form step (step 2 - we now only have 2 steps)
      setStep(2);
    }
  }, [isOpen, initialType]);

  // Get details for display
  const mainTypeDetails = selectedMainType ? findTypeById(selectedMainType) : null;
  const availableTags = selectedMainType ? getTagsForMainType(selectedMainType) : null;

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { title?: string; description?: string } = {};

    if (!title.trim()) {
      newErrors.title = "Title is required";
    } else if (title.length > 100) {
      newErrors.title = "Title must be 100 characters or less";
    }

    if (!description.plainText.trim()) {
      newErrors.description = "Description is required";
    } else if (description.markup.length > MAX_DESCRIPTION_LENGTH) {
      newErrors.description = `Description must be ${MAX_DESCRIPTION_LENGTH} characters or less`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-link status items belonging to the active worksheet when the type is
  // review_point. This runs whether the type was selected manually (step 1) or
  // pre-set via initialType (Excel ribbon button shortcut).
  useEffect(() => {
    if (selectedMainType === "review_point" && activeSheetId) {
      const matchingIds = statusItems
        .filter((item) => item.sheetId === activeSheetId)
        .map((item) => item.id);
      setSelectedStatusItemIds(matchingIds);
    }
  }, [selectedMainType, activeSheetId, statusItems]);

  // Handlers
  const handleMainTypeSelect = (typeId: string) => {
    setSelectedMainType(typeId);
    setSelectedTags([]); // Reset tags when changing main type

    // Clear status items if leaving review_point. The auto-link effect above
    // handles populating them when entering review_point.
    if (typeId !== "review_point") {
      setSelectedStatusItemIds([]);
    }

    // Go directly to form step (step 2)
    setStep(2);
  };

  const handleBack = () => {
    if (step === 2) {
      // Going back from form to type selection
      setSelectedMainType(null);
      setSelectedTags([]);
      setSelectedStatusItemIds([]);
      setStep(1);
    }
    setErrors({});
    setSubmitError(null);
  };

  const resetForm = () => {
    // Reset form fields but keep dialog open and preserve createAnother setting
    setStagedFiles([]);
    setCreatedDiscussionId(null);
    setSelectedTags([]);
    setSelectedStatusItemIds([]);
    setTitle("");
    setDescription({ markup: "", plainText: "" });
    setErrors({});
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    if (!selectedMainType) return;

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Only Reviewers and Partners are allowed to auto-flip linked status items
      // to "Rework Required" via the server. Preparers and Users send false so
      // their review points get linked but don't yank items backward through the
      // status flow. This matches the role-gating model used elsewhere in the
      // addin: frontend enforces; backend trusts the flag.
      const canAutoFlipToRework =
        userRole === UserRole.Reviewer || userRole === UserRole.Partner;

      const request: CreateDiscussionRequest = {
        spreadsheetId,
        title: title.trim(),
        type: selectedMainType,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        description: description.plainText.trim(),
        descriptionContent: description.markup,
        taggedUserEmails: extractUserEmails(description.markup),
        statusItemIds: selectedMainType === "review_point" && selectedStatusItemIds.length > 0
          ? selectedStatusItemIds
          : undefined,
        setLinkedStatusItemsToReworkRequired: canAutoFlipToRework,
      };

      // Reuse the discussion from a previous attempt whose attachment
      // uploads failed, so a retry doesn't create a duplicate.
      let discussionId = createdDiscussionId;
      if (discussionId == null) {
        const created = await createDiscussion(request);
        discussionId = created.id;
        setCreatedDiscussionId(discussionId);
        track("discussion.created", {
          type: selectedMainType,
          tagCount: selectedTags.length,
        });
      }

      // Attachments upload only once the discussion has an ID. A failure here
      // deliberately does NOT roll the discussion back — deleting it would
      // throw away everything the user typed, which is worse than a missing
      // image. The failed files stay staged so Create retries just those.
      if (enableDiscussionAttachments && stagedFiles.length > 0) {
        const failed: File[] = [];
        for (const file of stagedFiles) {
          try {
            await uploadDiscussionAttachment(discussionId, file);
          } catch {
            failed.push(file);
          }
        }
        if (failed.length > 0) {
          setStagedFiles(failed);
          // Refresh the list anyway — the discussion itself did get created.
          onSuccess();
          setSubmitError(
            `Created, but ${failed.length} attachment${failed.length === 1 ? "" : "s"} ` +
              `failed to upload (${failed.map((f) => f.name).join(", ")}). ` +
              "Press Retry upload, or close and add them from the discussion.",
          );
          return;
        }
        setStagedFiles([]);
      }

      // Notify parent of success
      onSuccess();

      if (createAnother) {
        // Reset form but keep dialog open
        resetForm();
      } else {
        // Close dialog
        handleClose();
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      setSubmitError(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStagedFiles([]);
    setCreatedDiscussionId(null);
    setStep(1);
    setSelectedMainType(null);
    setSelectedTags([]);
    setSelectedStatusItemIds([]);
    setTitle("");
    setDescription({ markup: "", plainText: "" });
    setErrors({});
    setSubmitError(null);
    setIsSubmitting(false);
    setCreateAnother(false);
    onDismiss();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_e, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
              />
            }
          >
            {mainTypeDetails ? `Create ${mainTypeDetails.name}` : "Create Discussion"}
          </DialogTitle>

          <DialogContent>
            {submitError && (
              <div className={styles.errorMessage}>
                <Text weight="semibold">Error creating discussion</Text>
                <br />
                <Text>{submitError}</Text>
              </div>
            )}

            {/* Step 1: Select main discussion type */}
            {step === 1 && (
              <DiscussionTypeSelector
                title="Select Discussion Type"
                types={MAIN_DISCUSSION_TYPES}
                selectedType={selectedMainType}
                onTypeSelect={handleMainTypeSelect}
              />
            )}

            {/* Step 2: Form with optional tag selection */}
            {step === 2 && (
              <>
                <div className={styles.selectedTypeHeader}>
                  <div
                    className={styles.typeIcon}
                    style={{ color: mainTypeDetails?.color || "#0078D4" }}
                  >
                    {mainTypeDetails && React.createElement(mainTypeDetails.icon)}
                  </div>
                  <Text weight="semibold">
                    {mainTypeDetails?.name}
                  </Text>
                </div>

                <DiscussionForm
                  title={title}
                  description={description}
                  onTitleChange={setTitle}
                  onDescriptionChange={setDescription}
                  users={users}
                  statusItems={statusItems}
                  errors={errors}
                  autoFocusTitle
                >
                  {/* Status item selection for Review Points only - rendered between title and description */}
                  {selectedMainType === "review_point" && statusItems.length > 0 && (
                    <StatusItemSelector
                      statusItems={statusItems}
                      selectedStatusItemIds={selectedStatusItemIds}
                      onStatusItemIdsChange={setSelectedStatusItemIds}
                    />
                  )}
                </DiscussionForm>

                {/* Tag selection for types that support tags */}
                {availableTags && availableTags.length > 0 && (
                  <TagSelector
                    title="Tags"
                    tags={availableTags}
                    selectedTags={selectedTags}
                    onTagsChange={setSelectedTags}
                  />
                )}

                {/* Staged here, uploaded by handleSubmit once the discussion
                    has an ID. Same feature flag as the post-creation panel. */}
                {enableDiscussionAttachments && (
                  <StagedAttachments
                    files={stagedFiles}
                    onFilesChange={setStagedFiles}
                    disabled={isSubmitting}
                  />
                )}
              </>
            )}
          </DialogContent>

          <DialogActions>
            {step === 1 ? (
              <Button appearance="secondary" onClick={handleClose}>
                Cancel
              </Button>
            ) : (
              <div className={styles.dialogActions}>
                <Checkbox
                  className={styles.createAnotherCheckbox}
                  checked={createAnother}
                  onChange={(_e, data) => setCreateAnother(data.checked === true)}
                  label="Create another"
                />
                <div className={styles.actionButtons}>
                  <Button
                    appearance="secondary"
                    icon={<ArrowLeft24Regular />}
                    onClick={handleBack}
                    disabled={isSubmitting}
                  >
                    Back
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting
                      ? "Creating..."
                      : createdDiscussionId != null
                        ? "Retry upload"
                        : "Create"}
                  </Button>
                </div>
              </div>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default CreateDiscussionDialog;
